import { useEffect, useState } from "react";
import { Modal } from "antd";
import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  SwapOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../../hooks/useViewport";
import { ChoiceRow } from "../../ChoiceRow";
import {
  GhostButton,
  ModalFooter,
  ModalHeader,
  posModalStyles as modalStyles,
} from "../../modalPrimitives";
import { depositTitleOf, type AttachedBillDeposit } from "./billDeposit";

type DepositChoice = "load" | "use";

interface DepositChoiceModalProps {
  open: boolean;
  onClose: () => void;
  onChoose: (choice: DepositChoice) => void;
  /** Защо „Използвай депозит" не може за избраната маса; null — може. */
  attachBlockedReason: string | null;
  /** Името на избраната маса; null — няма избрана. */
  tableName: string | null;
  /** Депозитът, вече закачен към избраната маса — вторият ред става „Смени
   *  или махни депозита" с него. */
  attached: AttachedBillDeposit | null;
}

/**
 * „Депозит" в „Опции" с двете права: два реда един под друг — „Създай нов
 * депозит" (гостът плаща сега, `DepositLoadModal`) и „Използвай депозит"
 * (вече платен депозит се закача към избраната маса, `DepositPickerModal`).
 * Надписът над заглавието казва откъде идват парите, за да не се бъркат двата.
 * Един клик избира; на десктоп и стрелки + Enter (⏎ стои на реда под
 * курсора), Esc затваря. „Използвай депозит" без отворена маса над 0.00 е
 * неактивен с причината под него.
 */
export function DepositChoiceModal({
  open,
  onClose,
  onChoose,
  attachBlockedReason,
  tableName,
  attached,
}: DepositChoiceModalProps) {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isDesktop } = useViewport();
  const [highlighted, setHighlighted] = useState<DepositChoice>("load");

  useEffect(() => {
    if (open) setHighlighted(attachBlockedReason ? "load" : "use");
  }, [open, attachBlockedReason]);

  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      } else if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) {
        event.preventDefault();
        if (attachBlockedReason) return;
        setHighlighted((current) => (current === "load" ? "use" : "load"));
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (highlighted === "use" && attachBlockedReason) return;
        onChoose(highlighted);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isDesktop, highlighted, attachBlockedReason, onChoose, onClose]);

  // Закаченият депозит (само към отворена маса — тогава има и име на маса).
  const useOption =
    attached && tableName
      ? {
          icon: <SwapOutlined />,
          kicker: t("depositAction.useKickerAttached", { table: tableName }),
          title: t("depositAction.useAttached"),
          description: t("depositAction.useHintAttached", {
            deposit: depositTitleOf(t, attached),
            amount: attached.amount,
          }),
        }
      : {
          icon: <MinusCircleOutlined />,
          kicker: t("depositAction.useKicker"),
          title: t("depositAction.use"),
          description: tableName
            ? t("depositAction.useHint", { table: tableName })
            : t("depositAction.useHintNoTable"),
        };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={isMobile ? undefined : 560}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<WalletOutlined />}
          title={t("depositAction.title")}
          subtitle={t("depositAction.subtitle")}
          onClose={onClose}
          isNarrow={isNarrow}
        />
        <div className={`flex flex-col gap-2.5 ${isNarrow ? "px-4 py-4" : "px-6 py-5"}`}>
          <ChoiceRow
            icon={<PlusCircleOutlined />}
            kicker={t("depositAction.loadKicker")}
            title={t("depositAction.load")}
            description={t("depositAction.loadHint")}
            highlighted={highlighted === "load"}
            showEnterHint={isDesktop && highlighted === "load"}
            isNarrow={isNarrow}
            onClick={() => onChoose("load")}
          />
          <ChoiceRow
            icon={useOption.icon}
            kicker={useOption.kicker}
            title={useOption.title}
            description={useOption.description}
            highlighted={highlighted === "use" && !attachBlockedReason}
            showEnterHint={isDesktop && highlighted === "use" && !attachBlockedReason}
            blockedReason={attachBlockedReason}
            isNarrow={isNarrow}
            onClick={() => onChoose("use")}
          />
        </div>
        <ModalFooter isNarrow={isNarrow}>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
}
